import React from 'react';
import { View } from 'react-native';
import { useTranslation } from '../../providers/TranslationProvider';
import { AppText } from '../AppText';
import styles from '../pantry/searchEmptyStateStyles';

export interface SiteSearchEmptyStateProps {
  query: string;
}

// Ports components/pantry/SearchEmptyState.jsx's copy and accessibility labeling for the new
// Sites/FreshTrak search flow.
export function SiteSearchEmptyState({ query }: SiteSearchEmptyStateProps) {
  const { t } = useTranslation();

  // SearchAllSites only matches by name or city, nationwide -- no radius, zip code, or street
  // address matching (see the endpoint's own doc comment: "where is the place called X", which
  // has no useful radius). The legacy multi-method copy (zip code/street address) overpromises
  // what's actually searchable here, so this uses its own scoped keys rather than reusing
  // search_accessibility_no_results[_generic] verbatim. Placeholders are `{query}`, not t()'s own
  // `{{param}}` interpolation convention -- substituted manually here, as the legacy component did.
  const accessibilityMessage = query
    ? t('search_accessibility_no_results_sites').replace('{query}', query)
    : t('search_accessibility_no_results_sites_generic');
  const noSitesMatch = t('search_no_pantries_match').replace('{query}', query);

  return (
    <View style={styles.container} accessibilityLabel={accessibilityMessage} accessibilityRole="text">
      <AppText style={styles.title} accessibilityRole="header">
        {t('search_no_results_found')}
      </AppText>
      {query && (
        <AppText style={styles.subtitle} accessibilityLabel={noSitesMatch}>
          {noSitesMatch}
        </AppText>
      )}
      <AppText style={styles.suggestion} accessibilityRole="header">
        {t('search_try_searching_by')}
      </AppText>
      <AppText style={styles.suggestionItem} accessibilityLabel={t('search_suggestion_city_accessibility')}>
        • {t('search_suggestion_city')}
      </AppText>
      <AppText style={styles.suggestionItem} accessibilityLabel={t('search_suggestion_pantry_name')}>
        • {t('search_suggestion_pantry_name')}
      </AppText>
    </View>
  );
}
